import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentSettingsService } from 'components/api/paymentSettings';
import { PaymentSetting, CreatePaymentSettingDto, UpdatePaymentSettingDto } from 'types/paymentSetting';

interface PaymentSettingsState {
  data: PaymentSetting[];
  isLoading: boolean;
  error: string | null;
}

const initialState: PaymentSettingsState = {
  data: [],
  isLoading: false,
  error: null,
};

export const fetchPaymentSettings = createAsyncThunk(
  'paymentSettings/fetchAll',
  async (siteId: string | number, { rejectWithValue }) => {
    try {
      const response = await paymentSettingsService.getSettings(siteId);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Error fetching payment settings');
    }
  }
);

export const createPaymentSetting = createAsyncThunk(
  'paymentSettings/create',
  async (data: CreatePaymentSettingDto, { rejectWithValue }) => {
    try {
      const response = await paymentSettingsService.createSetting(data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Error creating payment setting');
    }
  }
);

export const updatePaymentSetting = createAsyncThunk(
  'paymentSettings/update',
  async ({ id, data }: { id: number; data: UpdatePaymentSettingDto }, { rejectWithValue }) => {
    try {
      const response = await paymentSettingsService.updateSetting(id, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Error updating payment setting');
    }
  }
);

export const deletePaymentSetting = createAsyncThunk(
  'paymentSettings/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await paymentSettingsService.deleteSetting(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Error deleting payment setting');
    }
  }
);

const paymentSettingsSlice = createSlice({
  name: 'paymentSettings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPaymentSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        
        let parsedData = [];
        if (Array.isArray(action.payload)) {
          parsedData = action.payload;
        } else if (action.payload?.data?.settings) {
          parsedData = action.payload.data.settings;
        } else if (action.payload?.data?.data) {
          parsedData = action.payload.data.data;
        } else if (action.payload?.data) {
          parsedData = Array.isArray(action.payload.data) ? action.payload.data : (action.payload.data.docs || []);
        }
        
        state.data = parsedData;
      })
      .addCase(fetchPaymentSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createPaymentSetting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPaymentSetting.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data.push(action.payload?.data || action.payload);
      })
      .addCase(createPaymentSetting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updatePaymentSetting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePaymentSetting.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.data.findIndex((s) => s.id === (action.payload?.data?.id || action.payload?.id));
        if (index !== -1) {
          state.data[index] = action.payload?.data || action.payload;
        }
      })
      .addCase(updatePaymentSetting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deletePaymentSetting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePaymentSetting.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = state.data.filter((s) => s.id !== action.payload);
      })
      .addCase(deletePaymentSetting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = paymentSettingsSlice.actions;

export default paymentSettingsSlice.reducer;
